<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-2xl mx-auto px-4 py-10">
      <div v-if="pageLoading" class="bg-white rounded-lg shadow-sm p-10 text-center text-gray-500">Carregando…</div>

      <div v-else-if="!meta.valid" class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center gap-3 mb-4">
          <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
        </div>
        <h1 class="text-2xl font-bold text-gray-900">Cadastro de dados</h1>
        <p class="text-sm text-red-700 mt-4">{{ (meta.errors && meta.errors[0]) || 'Link inválido ou indisponível.' }}</p>
      </div>

      <div v-else-if="submitted" class="bg-white rounded-lg shadow-sm p-6 text-center">
        <div class="flex items-center justify-center gap-3 mb-4">
          <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
        </div>
        <p class="text-green-700 font-semibold text-lg">{{ successMessage }}</p>
      </div>

      <div v-else class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center gap-3 mb-4">
          <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
        </div>

        <div class="mb-6">
          <h1 class="text-2xl font-bold text-gray-900">Cadastro de hospede</h1>
          <p class="text-sm text-gray-700 mt-4 leading-relaxed">
            Olá, <span class="font-semibold text-gray-900">{{ meta.greeting_name }}</span>, tudo bem?
            <span class="font-semibold text-gray-900">{{ meta.inviter_name }}</span> por aqui!
            <br>
            Preencha seus dados com atenção para concluirmos seu cadastro 😊
          </p>
        </div>

        <form class="space-y-4" @submit.prevent="submit">
          <p
            v-if="formError"
            class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
          >
            {{ formError }}
          </p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
              <input
                v-model="form.name"
                type="text"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
              <input
                v-model="form.email"
                type="email"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
              <input
                v-model="form.phone"
                type="tel"
                required
                maxlength="15"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                @input="applyPhoneMask"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">CPF/CNPJ *</label>
              <input
                v-model="form.document"
                type="text"
                required
                maxlength="18"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                @input="applyDocumentMask"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">RG *</label>
              <input
                v-model="form.rg"
                type="text"
                required
                maxlength="12"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                @input="applyRgMask"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Profissão *</label>
              <input
                v-model="form.profession"
                type="text"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Estado civil *</label>
              <select
                v-model="form.marital_status"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="" disabled>Selecione</option>
                <option value="Solteiro(a)">Solteiro(a)</option>
                <option value="Casado(a)">Casado(a)</option>
                <option value="Divorciado(a)">Divorciado(a)</option>
                <option value="Viúvo(a)">Viúvo(a)</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nacionalidade *</label>
              <input
                v-model="form.nationality"
                type="text"
                required
                placeholder="Ex: brasileiro(a)"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">CEP *</label>
              <input
                v-model="form.zip"
                type="text"
                required
                maxlength="9"
                placeholder="00000-000"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                @input="onZipInput"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Endereço *</label>
              <input
                v-model="form.address"
                type="text"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Número *</label>
              <input
                v-model="form.number"
                type="text"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Bairro *</label>
                <input
                  v-model="form.neighborhood"
                  type="text"
                  required
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <CitySelectAsync
                  v-model="form.city_id"
                  :selected-label="form.city_label || ''"
                  label="Cidade"
                  required
                  placeholder="Digite para buscar cidade..."
                  @city-picked="onCityPicked"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <p class="text-xs text-gray-500">
              <template v-if="expiresHint">Este link é válido até {{ expiresHint }}.</template>
              <template v-else>Campos marcados com * são obrigatórios.</template>
            </p>
            <button
              type="submit"
              :disabled="saving"
              class="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium disabled:opacity-50 w-full sm:w-auto shrink-0"
            >
              {{ saving ? 'Enviando…' : 'Enviar cadastro' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useBrazilianMasks } from '../composables/useBrazilianMasks.js'
import CitySelectAsync from './CitySelectAsync.vue'

const { handlePhoneInput, handleDocumentInput, handleRgInput } = useBrazilianMasks()

/** Valor inicial do campo nacionalidade (o hóspede pode alterar). */
const DEFAULT_NATIONALITY = 'Brasileiro'

export default {
  name: 'PublicGuestPersonRegister',
  components: { CitySelectAsync },
  props: {
    token: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      logoUrl: '/tripday-logo.png',
      pageLoading: true,
      meta: { valid: false, errors: [], greeting_name: '', inviter_name: '', expires_at: '' },
      form: {
        name: '',
        email: '',
        phone: '',
        document: '',
        rg: '',
        profession: '',
        marital_status: '',
        nationality: DEFAULT_NATIONALITY,
        zip: '',
        address: '',
        number: '',
        neighborhood: '',
        city_id: '',
        city_label: ''
      },
      formError: '',
      saving: false,
      submitted: false,
      successMessage: ''
    }
  },

  computed: {
    expiresHint() {
      const iso = this.meta.expires_at
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    }
  },

  watch: {
    token() {
      this.bootstrap()
    }
  },

  mounted() {
    this.bootstrap()
  },

  methods: {
    async bootstrap() {
      this.pageLoading = true
      this.formError = ''
      this.submitted = false
      try {
        const res = await fetch(`/api/v1/public/guest_person_register/${encodeURIComponent(this.token)}`)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          this.meta = { valid: false, errors: data.errors || ['Link inválido.'] }
        } else {
          this.meta = data
        }
      } catch {
        this.meta = { valid: false, errors: ['Não foi possível carregar o formulário.'] }
      } finally {
        this.pageLoading = false
      }
    },

    applyPhoneMask(e) {
      handlePhoneInput(e, this.form)
    },

    applyDocumentMask(e) {
      handleDocumentInput(e, this.form)
    },

    applyRgMask(e) {
      handleRgInput(e, this.form)
    },

    async onZipInput(e) {
      const input = e.target
      const digits = input.value.replace(/\D/g, '')
      input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5, 8)}` : digits
      this.form.zip = input.value
      if (digits.length !== 8) return

      let data
      try {
        const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`)
        data = await res.json()
      } catch {
        return
      }
      if (!data || data.erro) return
      if (data.logradouro) this.form.address = data.logradouro
      if (data.bairro) this.form.neighborhood = data.bairro

      const nameNoAccent = String(data.localidade || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
      const uf = String(data.uf || '')
        .trim()
        .toUpperCase()
      if (!nameNoAccent || uf.length !== 2) return

      const params = new URLSearchParams({ q: nameNoAccent.slice(0, 40) })
      params.set('uf', uf)
      const resCity = await fetch(`/api/v1/cities/search?${params.toString()}`)
      if (!resCity.ok) return
      const payload = await resCity.json()
      const list = payload.cities || []
      if (!list.length) return
      const c = list[0]
      const fullLabel = c.label || (c.uf ? `${c.display_name || c.name} - ${c.uf}` : c.display_name || c.name)
      this.form.city_id = c.id
      this.form.city_label = fullLabel
    },

    onCityPicked({ label }) {
      this.form.city_label = label || ''
    },

    async submit() {
      this.formError = ''
      if (!this.form.city_id) {
        this.formError = 'Selecione a cidade.'
        return
      }
      this.saving = true
      try {
        const payload = {
          person: {
            ...this.form,
            city_id: this.form.city_id
          }
        }
        const res = await fetch(`/api/v1/public/guest_person_register/${encodeURIComponent(this.token)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        })
        const data = await res.json().catch(() => ({}))
        if (res.ok) {
          this.submitted = true
          this.successMessage = data.message || 'Cadastro concluído com sucesso. Obrigado!'
        } else {
          const errs = data.errors
          this.formError = Array.isArray(errs) ? errs.join(' ') : data.error || 'Não foi possível enviar.'
        }
      } catch {
        this.formError = 'Erro de rede. Tente novamente.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
